"use client";

import React, { useState, useEffect, useMemo, memo, useCallback } from "react";
import ProgressiveImage from "./image";
import { selectImageByType, getResponsiveSizes, detectDevice } from "@/helpers/imageSelector";

const ResponsiveImage = memo(({
  contentPhotos,
  imageType,
  alt,
  className,
  style,
  onLoad,
  onError,
  priority = false,
  forceDevice = null,
  disableSkeleton = false,
  fallbackSrc,
  ...props
}) => {
  // Start with null so SSR renders skeleton only; real device detected on CSR
  const [currentDevice, setCurrentDevice] = useState(null);

  // Optimized device detection with debounced resize
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const updateDevice = () => {
      setCurrentDevice(detectDevice());
    };

    // Set initial device
    updateDevice();

    // Debounced resize listener
    let timeoutId;
    const debouncedUpdateDevice = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(updateDevice, 150);
    };

    window.addEventListener('resize', debouncedUpdateDevice, { passive: true });
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', debouncedUpdateDevice);
    };
  }, []);

  // SSR: null (skeleton shown) → CSR: full device-specific image
  const selectedImage = useMemo(() => {
    const targetDevice = forceDevice || currentDevice;

    if (!targetDevice) {
      // SSR / pre-hydration: return null, skeleton shown until CSR picks device
      return null;
    }

    return selectImageByType(contentPhotos, imageType, targetDevice);
  }, [contentPhotos, imageType, currentDevice, forceDevice]);

  // Generate responsive sizes
  const responsiveSizes = useMemo(() => {
    return getResponsiveSizes(imageType);
  }, [imageType]);

  // Handle missing image
  if (!selectedImage) {
    if (fallbackSrc) {
      return (
        <img
          src={fallbackSrc}
          alt={alt || ""}
          loading={priority ? "eager" : "lazy"}
          className={className}
          style={style}
          onError={(event) => {
            event.currentTarget.onerror = null;
            if (event.currentTarget.getAttribute("src") !== "/placeholder.jpg") {
              event.currentTarget.src = "/placeholder.jpg";
            }
          }}
        />
      );
    }
    return (
      <div
        className={`flex items-center justify-center bg-gray-800 text-gray-400 ${className || ''}`}
        style={{ aspectRatio: "16/9", ...style }}
      >
        <span className="text-sm">No {imageType} available</span>
      </div>
    );
  }

  return (
    <ProgressiveImage
      imageData={selectedImage}
      alt={alt}
      className={className}
      style={style}
      onLoad={onLoad}
      onError={onError}
      sizes={responsiveSizes}
      disableSkeleton={disableSkeleton}
      {...props}
    />
  );
});

ResponsiveImage.displayName = 'ResponsiveImage';

export default ResponsiveImage;
